<template>
    <RegularModal ref="modal" modal-card-class="is-medium">
        <template v-slot:title>
            {{ isEditMode ? translate("coupons.edit_coupon") : translate("coupons.add_new_coupon") }}
        </template>
        <template v-slot:body>
            <div class="field" :class="{ 'has-error': codeError }">
                <label class="label">{{ translate("coupons.code") }} *</label>
                <div class="field has-addons">
                    <div class="control is-expanded">
                        <input
                            class="input"
                            type="text"
                            v-model="code"
                            :placeholder="translate('coupons.code_placeholder')"
                            :readonly="isEditMode"
                        />
                    </div>
                    <div class="control">
                        <button class="button" @click="generateCode" :disabled="isEditMode">
                            <span class="icon"><i class="fas fa-magic"></i></span>
                            <span>{{ translate("coupons.generate") }}</span>
                        </button>
                    </div>
                </div>
                <p class="help" v-if="codeError">{{ codeError }}</p>
            </div>

            <div
                class="field"
                :class="{
                    'has-error': discountPercentageError,
                    'has-warning': !discountPercentageError && discountPercentageWarning,
                }"
            >
                <label class="label">{{ translate("coupons.discount_percentage") }} *</label>
                <div class="control">
                    <input class="input" type="number" v-model="discountPercentage" placeholder="0-100" step="1" min="0" max="100" />
                </div>
                <p class="help" v-if="discountPercentageError">{{ discountPercentageError }}</p>
                <p class="help" v-else-if="discountPercentageWarning">{{ discountPercentageWarning }}</p>
                <p class="help" v-else>{{ translate("coupons.percentage_help") }}</p>
            </div>

            <div class="field" :class="{ 'has-error': maxUsesError }">
                <label class="label">{{ translate("coupons.max_uses") }}</label>
                <div class="control">
                    <input
                        class="input"
                        type="number"
                        v-model.number="maxUses"
                        :placeholder="translate('coupons.unlimited_placeholder')"
                        min="1"
                    />
                </div>
                <p class="help" v-if="maxUsesError">{{ maxUsesError }}</p>
                <p class="help" v-else>{{ translate("coupons.max_uses_help") }}</p>
            </div>

            <div class="field">
                <label class="label">{{ translate("coupons.usage_type") }} *</label>
                <div class="control">
                    <div class="select is-fullwidth">
                        <select v-model="usageType" :disabled="isEditMode">
                            <option value="shared">{{ translate("coupons.usage_type_shared") }}</option>
                            <option value="personal">{{ translate("coupons.usage_type_personal") }}</option>
                        </select>
                    </div>
                </div>
                <p class="help">{{ translate("coupons.usage_type_help") }}</p>
            </div>

            <div class="field" :class="{ 'has-error': validForDaysError }">
                <label class="label">{{ translate("coupons.valid_for_days") }}</label>
                <div class="control">
                    <input
                        class="input"
                        type="number"
                        v-model.number="validForDays"
                        :placeholder="translate('coupons.valid_for_days_placeholder')"
                        min="1"
                    />
                </div>
                <p class="help" v-if="validForDaysError">{{ validForDaysError }}</p>
                <p class="help" v-else>
                    {{ translate(isEditMode ? "coupons.valid_for_days_help_edit" : "coupons.valid_for_days_help") }}
                </p>
            </div>

            <div class="field">
                <Checkbox2 v-model="active">
                    {{ translate("coupons.active") }}
                </Checkbox2>
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">
                    <span class="icon"><i class="fas fa-times"></i></span>
                    <span>{{ translate("common.cancel") }}</span>
                </button>
                <LoadingButton class="button is-primary" :is-loading="saving" @click="save" :disabled="!isValid">
                    <span class="icon"><i class="fas fa-save"></i></span>
                    <span>{{ translate("common.save") }}</span>
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import RegularModal from "../../Common/RegularModal.vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import Checkbox2 from "../../Common/Checkbox2.vue";
    import { translate } from "../../Common/translations";
    import { useModal } from "../../Common/useModal";
    import couponsApi, { Coupon, CouponUsageType, CreateCoupon } from "../../Api/coupons";
    import { handleError } from "../../Common/errorHandler";
    import { successToast } from "../../Common/toasts";
    import {
        convertFromTimezone,
        convertToTimezone,
        getCurrentDateTimeInTimezone,
        parseDateTimeInTimezone,
        calculateDaysDifference,
        addDaysToCurrentTime,
    } from "../../Common/dateTimeUtils";
    import { isIntegerGreaterThan0 } from "../../Common/numbers";

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
    }>();

    const emit = defineEmits<{
        saved: [];
    }>();

    const { open: openModal, close, modal } = useModal();

    const isEditMode = ref(false);
    const saving = ref(false);
    const editingCouponId = ref<number | null>(null);
    const validForDays = ref<number | null>(null);

    // Form fields as direct refs
    const code = ref("");
    const discountPercentage = ref(10);
    const maxUses = ref<number | null>(null);
    const usageType = ref<CouponUsageType>("shared");
    const validUntil = ref<string | null>(null);
    const active = ref(true);

    const codeError = computed(() => {
        if (!code.value || code.value.trim() === "") {
            return translate("coupons.code_required");
        }
        if (code.value.length > 50) {
            return translate("coupons.code_too_long");
        }
        return null;
    });

    const discountPercentageError = computed(() => {
        if (!isIntegerGreaterThan0(discountPercentage.value) || discountPercentage.value >= 100) {
            return translate("coupons.discount_percentage_invalid");
        }
        return null;
    });

    const discountPercentageWarning = computed(() => {
        if (discountPercentage.value >= 50 && discountPercentage.value < 100) {
            return translate("coupons.discount_percentage_high_warning");
        }
        return null;
    });

    const maxUsesError = computed(() => {
        if (maxUses.value !== null && !isIntegerGreaterThan0(maxUses.value)) {
            return translate("coupons.max_uses_invalid");
        }
        return null;
    });

    const validForDaysError = computed(() => {
        if (validForDays.value !== null && !isIntegerGreaterThan0(validForDays.value)) {
            return translate("coupons.valid_for_days_invalid");
        }
        return null;
    });

    const isValid = computed(() => {
        return !codeError.value && !discountPercentageError.value && !maxUsesError.value && !validForDaysError.value;
    });

    function calculateValidForDays(validUntilStr: string | null): number | null {
        if (!validUntilStr) {
            return null;
        }

        // Convert UTC validUntil to event timezone
        const validUntilInTimezone = convertToTimezone(validUntilStr, props.eventTimezone, "yyyy-MM-dd HH:mm:ss");
        const now = getCurrentDateTimeInTimezone(props.eventTimezone);
        const validUntilMoment = parseDateTimeInTimezone(validUntilInTimezone, "yyyy-MM-dd HH:mm:ss", props.eventTimezone);

        const diffInDays = calculateDaysDifference(validUntilMoment, now);
        return diffInDays > 0 ? diffInDays : null;
    }

    const open = () => {
        isEditMode.value = false;
        editingCouponId.value = null;
        resetForm();
        generateCode();
        openModal();
    };

    const openForEdit = (coupon: Coupon) => {
        isEditMode.value = true;
        editingCouponId.value = coupon.id;
        code.value = coupon.code;
        discountPercentage.value = coupon.discountPercentage;
        maxUses.value = coupon.maxUses;
        usageType.value = coupon.usageType;
        validUntil.value = coupon.validUntil;
        active.value = coupon.active;

        validForDays.value = calculateValidForDays(coupon.validUntil);

        openModal();
    };

    const resetForm = () => {
        code.value = "";
        discountPercentage.value = 10;
        maxUses.value = null;
        usageType.value = "shared";
        validUntil.value = null;
        validForDays.value = null;
        active.value = true;
    };

    const generateCode = () => {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        let generatedCode = "";
        for (let i = 0; i < 8; i++) {
            generatedCode += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        code.value = generatedCode;
    };

    const save = () => {
        saving.value = true;

        let calculatedValidUntil = validUntil.value;

        // Calculate validUntil from validForDays if provided
        if (validForDays.value) {
            // Start from now in the event timezone and add days
            const futureDateStr = addDaysToCurrentTime(validForDays.value, props.eventTimezone, "yyyy-MM-dd HH:mm:ss");
            // Convert to UTC using convertFromTimezone
            calculatedValidUntil = convertFromTimezone(futureDateStr, props.eventTimezone);
        }

        const requestData: CreateCoupon = {
            code: code.value,
            discountPercentage: discountPercentage.value,
            maxUses: maxUses.value,
            usageType: usageType.value,
            validUntil: calculatedValidUntil,
            active: active.value,
        };

        const apiCall =
            isEditMode.value && editingCouponId.value
                ? couponsApi.updateCoupon(props.eventId, editingCouponId.value, {
                      id: editingCouponId.value,
                      ...requestData,
                  })
                : couponsApi.createCoupon(props.eventId, requestData);

        return apiCall
            .then(() => {
                successToast(isEditMode.value ? translate("coupons.updated_successfully") : translate("coupons.created_successfully"));
                emit("saved");
                close();
            })
            .catch(handleError)
            .finally(() => {
                saving.value = false;
            });
    };

    defineExpose({ open, openForEdit });
</script>

<style scoped></style>
